import styled from '@emotion/styled';
import { Colors } from 'msteams-ui-styles-core/lib/colors';
import { Ref } from 'react';
import { ThemeString } from '../../context';
import {
  canvasBackgroundColor,
  primaryButtonColor,
  primaryTextColor,
  separatorColor,
} from '../../styles/SharedStyles';

export interface LoginContainerDivProps {
  themeStr: string;
}

export interface SelectedItemStyleProps {
  selected: boolean;
  themeStr?: string;
  ref?: Ref<HTMLLIElement>;
  hasBorder: boolean;
  index: number;
  item?: any;
  disabled?: boolean;
}

interface StyledLabelProps {
  themeStr: string;
}

interface ButtonLabelProps {
  disabled: boolean;
  themeStr: string;
}

const listItemPadding = ({ hasBorder }) => (hasBorder ? '8px 6px' : '6px');

export const WizardActions = styled('div')`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  height: 30px;
  padding-top: 0.5rem;
  flex: auto;
`;

export const StyledListContainer = styled('div')`
  height: 100%;
  display: inline-flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const StyledListItem = styled('li')<SelectedItemStyleProps>`
  background-color: ${props => determineLiBackgroundColor(props)};
  color: ${props => determineTextColor(props)};
  cursor: ${props => (props.item.disabled ? 'default' : 'pointer')};
  box-sizing: border-box;
  padding: ${props => listItemPadding(props)};
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: ${props =>
    props.item && props.item.rightButton ? 'space-between' : 'flex-start'};
  align-items: center;
  flex-shrink: 0;
  opacity: ${props => (props.item.disabled ? '0.6' : '1')};
  &:hover {
    color: ${props =>
      props.item.disabled ? '' : determineTextHoverColor(props)};
    background-color: ${props =>
      props.item.disabled ? '' : determineTextHoverBgColor(props)};
  }
  #008be1 &:last-of-type::after {
    display: none;
  }

  ${({ hasBorder, themeStr }) => {
    return (
      hasBorder &&
      `
      &::after {
        content: '';
        display: block;
        width: calc(100% - 14px);
        height: 0px;
        background-color: ${separatorColor(themeStr as ThemeString)};
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        box-sizing: border-box;
        margin: auto;
        margin-bottom: -0.5px;
      }
    `
    );
  }} p {
    font-size: 12px;
    margin: 0px;
    padding: 0px;
  }
`;

export const StyledLabeledListItem = styled('li')<SelectedItemStyleProps>`
  background-color: ${props => determineLiBackgroundColor(props)};
  color: ${props => determineTextColor(props)};
  box-sizing: border-box;
  padding: ${props => listItemPadding(props)};
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: ${props =>
    props.item && props.item.rightButton ? 'space-between' : 'flex-start'};
  align-items: center;
  flex-shrink: 0;
  opacity: ${props => (props.item.disabled ? '0.6' : '1')};
  #008be1 &:last-of-type::after {
    display: none;
  }

  ${({ hasBorder, themeStr }) => {
    return (
      hasBorder &&
      `
      &::after {
        content: '';
        display: block;
        width: calc(100% - 14px);
        height: 0px;
        background-color: ${separatorColor(themeStr as ThemeString)};
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        box-sizing: border-box;
        margin: auto;
        margin-bottom: -0.5px;
      }
    `
    );
  }} p {
    font-size: 12px;
    margin: 0px;
    padding: 0px;
  }
`;

export const StyledLabel = styled('p')<StyledLabelProps>`
  margin: 9px 0 0 0;
  font-size: 16px;
  text-align: left;
  border-bottom: 0.06666666666666667rem solid
    ${({ themeStr }) => separatorColor(themeStr as ThemeString)};
  color: ${({ themeStr }) => primaryButtonColor(themeStr as ThemeString)};
  padding-bottom: 9px;
  span {
    border-bottom: 0.26666666666666666rem solid
      ${({ themeStr }) => primaryButtonColor(themeStr as ThemeString)};
    padding-bottom: 5px;
  }
`;

export const StyledGridContainer = styled('div')`
  height: 100%;
  display: inline-flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const StyledGridItem = styled('li')<SelectedItemStyleProps>`
  background-color: ${props => determineLiBackgroundColor(props)};
  color: ${props => determineTextColor(props)};
  cursor: ${props => (props.item.disabled ? 'default' : 'pointer')};
  box-sizing: border-box;
  padding: ${props => listItemPadding(props)};
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  margin: 0px;
  padding: 10px 0px 0px 10px;
  opacity: ${props => (props.item.disabled ? '0.6' : '1')};
  &:hover {
    color: ${props =>
      props.item.disabled ? '' : determineTextHoverColor(props)};
    background-color: ${props =>
      props.item.disabled ? '' : determineTextHoverBgColor(props)};
  }
  #008be1 &:last-of-type::after {
    display: none;
  }

  ${({ hasBorder, themeStr }) => {
    return (
      hasBorder &&
      `
      &::after {
        content: '';
        display: block;
        width: calc(100% - 14px);
        height: 1px;
        background-color: ${separatorColor(themeStr as ThemeString)};
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        box-sizing: border-box;
        margin: auto;
        margin-bottom: -0.5px;
      }
    `
    );
  }} p {
    font-size: 12px;
    margin: 0px;
    padding: 0px;
  }
`;

export const ButtonLabel = styled('span')`
  display: inline;
  position: relative;
  top: -1px;
  color: ${(props: ButtonLabelProps) =>
    determineBtnTextColor(props.disabled, props.themeStr)};
  font-size: 16px;
  margin: 0 8px;
`;

export const ProjectTag = styled('span')<{ type?: string }>`
  padding: 0.2em 0.6em 0.3em;
  font-size: 75%;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25em;
  margin-left: 5px;
  background-color: ${props =>
    props.type === 'pw365' ? '#008be1' : '#a3779f'};
  display: inline-block;
  letter-spacing: 0.03em;
  position: relative;
  top: -1px;
`;

export const LoginContainerDiv = styled('div')<LoginContainerDivProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  box-sizing: border-box;
  background: ${({ themeStr }) =>
    canvasBackgroundColor(themeStr as ThemeString)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
  font-family: 'Segoe UI, Open Sans';
  color: ${({ themeStr }) => primaryTextColor(themeStr as ThemeString)};
  a {
    color: ${({ themeStr }) => primaryTextColor(themeStr as ThemeString)};
    &:visited {
      color: ${({ themeStr }) => primaryTextColor(themeStr as ThemeString)};
    }
  }
`;
export const FooterContainer = styled('div')`
  display: flex;
  justify-content: space-around;
  flex-direction: row;
  font-family: Segoe UI, Open Sans;
  font-size: 10px;
  width: 360px;

  a {
    color: #85929d;
    text-decoration: none;

    &:visited {
      color: #85929d;
    }

    &:hover {
      text-decoration: underline;
    }
  }
`;
export const IconContainer = styled('span')<{ themeStr: string }>`
  width: 20px;
  height: 20px;
  display: block;
  margin-right: 5px;
  background-color: ${({ themeStr }) =>
    themeStr === 'dark' ? 'transparent' : 'transparent'};
`;

export const FileDownloadWarning = styled('a')`
  color: ${Colors.light.brand00};
  text-decoration: none;
  outline: none;
`;

export const AuxButton = styled('button')`
  border: none;
  background-color: ${Colors.light.brand08};
  cursor: pointer;

  padding: 4px 7px;
  border-radius: 3px;
  color: white;

  &:hover {
    background-color: ${Colors.light.brand00};
  }

  &:active {
    background-color: ${Colors.light.brand04};
  }
`;

const determineTextColor = ({ selected, themeStr }: SelectedItemStyleProps) => {
  const defaultColor = 'white';

  switch (themeStr) {
    case 'contrast':
      return selected ? Colors.highContrast.black : defaultColor;
    case 'dark':
      return defaultColor;
    default:
      return selected ? defaultColor : 'black';
  }
};

const determineTextHoverColor = ({
  selected,
  themeStr,
}: SelectedItemStyleProps) => {
  switch (themeStr) {
    case 'dark':
      return 'white';
    case 'contrast':
      return 'black';
    default:
      return selected ? 'white' : 'black';
  }
};

const determineTextHoverBgColor = ({
  selected,
  themeStr,
}: SelectedItemStyleProps) => {
  const defaultColor = 'white';

  const normalizedTheme = normalizeTheme(themeStr);

  if (
    !normalizedTheme ||
    normalizedTheme === 'contrast' ||
    (typeof normalizedTheme === 'object' &&
      !Object.keys(normalizedTheme).length)
  )
    return defaultColor;

  return selected
    ? Colors[normalizedTheme].brand00
    : Colors[normalizedTheme].brand14;
};

const determineLiBackgroundColor = ({
  themeStr,
  selected,
}: SelectedItemStyleProps) => {
  if (themeStr === 'contrast') {
    return selected ? Colors.highContrast.yellow : Colors.highContrast.black;
  }
  return selected ? Colors.light.brand00 : 'transparent';
};

const normalizeTheme = theme => {
  if (theme === 'default') {
    return 'light';
  }
  return theme;
};

const determineBtnTextColor = (disabled: boolean, theme: string) => {
  if (theme === 'dark')
    return disabled ? Colors.dark.gray04 : Colors.dark.white;
  if (theme === 'contrast')
    return disabled ? Colors.highContrast.green : Colors.highContrast.green;
  return disabled ? Colors.light.gray08 : Colors.light.brand00;
};
